import React, { useState } from 'react';
import ImageWithFallback from './ImageWithFallback';
import ImagePreviewModal from './ImagePreviewModal';
import { MessageCircle, Share2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import RedditLogo from './RedditLogo';
import UpvoteIcon from './UpvoteIcon';
import DownvoteIcon from './DownvoteIcon';
import PostIcon from './PostIcon';
import NSFWIcon from './NSFWIcon';
import './RedditCard.css';
import SearchLink from './SearchLink';
import { useStagingTray } from '../../context/StagingTrayContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useDevMode } from '../../context/DevModeContext';
import FavoriteStar from './FavoriteStar';

const RedditCard = ({ post, isInsightsView = false }) => {
  const { addItem, removeItem, isInTray } = useStagingTray();
  const { toggleFavorite } = useFavorites();
  const { isDevMode } = useDevMode();
  const inTray = isInTray(post);
  const [previewImage, setPreviewImage] = useState(null);
  const longPressTimer = React.useRef(null);
  const [isLongPressing, setIsLongPressing] = React.useState(false);

  const handleLongPressStart = (e) => {
    console.log('RedditCard: Long press start', { isDevMode, postId: post.id });
    if (!isDevMode || isInsightsView) return;
    
    setIsLongPressing(true);
    longPressTimer.current = setTimeout(() => {
      console.log('RedditCard: Long press triggered, toggling favorite for', post.id);
      toggleFavorite(post.id);
      setIsLongPressing(false);
    }, 500); // 500ms long press
  };

  const handleLongPressEnd = (e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setIsLongPressing(false);
  };

  const handleCardClick = (e) => {
    // Don't toggle if clicking on interactive elements or in insights view
    if (e.target.closest('a, button') || isInsightsView) {
      return;
    }
    
    if (inTray) {
      removeItem(post);
    } else {
      addItem(post);
    }
  };

  if (isInsightsView) {
    return (
      <div className="reddit-card reddit-insights-view">
        <div className="reddit-card-content">
          <div className="flex items-start">
            <div className="reddit-vote-section">
              <div className="reddit-upvote-button reddit-vote-button">
                <UpvoteIcon size={24} className="reddit-upvote-icon" />
              </div>
              <span className="reddit-vote-count">{post.ups}</span>
              <div className="reddit-downvote-button reddit-vote-button">
                <DownvoteIcon size={24} className="reddit-downvote-icon" />
              </div>
            </div>
            <div className="reddit-post-content">
              <div className="reddit-icon-container">
                <PostIcon size={40} />
              </div>
              <ImageWithFallback
                src={post.thumbnail}
                localSrc={post.localThumbnail}
                alt="Post thumbnail"
                className="reddit-post-thumbnail"
              />

              <div>

                <h3 className="reddit-post-title">
                  <a href={post.url} target="_blank" rel="noopener noreferrer" className="reddit-link">
                    {post.title}
                  </a>
                </h3>
                <p className="reddit-post-meta">
                  Posted by
                  <a href={`https://www.reddit.com/user/${post.author}/`} target="_blank" rel="noopener noreferrer" className="reddit-link">
                    u/{post.author}
                  </a>
                  {' in '}
                  <a href={`https://www.reddit.com/r/${post.subreddit}/`} target="_blank" rel="noopener noreferrer" className="reddit-link">
                    r/{post.subreddit}
                  </a>
                </p>
              </div>
              <div className="reddit-post-footer">
                <a href={post.url} target="_blank" rel="noopener noreferrer" className="reddit-footer-action">
                  <MessageCircle size={18} className="mr-2" />
                  <span>{post.num_comments} comments</span>
                </a>
                <button className="reddit-footer-action">
                  <Share2 size={18} className="mr-2" />
                  <span>Share</span>
                </button>
                <a
                  href={post.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="reddit-footer-action"
                  aria-label="Visit Reddit"
                >
                  <RedditLogo size={16} />
                  <span className="sr-only">Reddit</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isComment = post._replies && Array.isArray(post._replies) && post._replies.length > 0;

  // Check if the post has an image URL or if the post URL is an image
  const isImageUrl = !!(post.image_url && (
    post.image_url.match(/\.(jpeg|jpg|gif|png|webp)$/i) ||
    post.image_url.includes('i.redd.it') ||
    post.image_url.includes('i.imgur.com')
  )) || !!(post.url && (
    post.url.match(/\.(jpeg|jpg|gif|png|webp)$/i) ||
    post.url.includes('i.redd.it') ||
    post.url.includes('i.imgur.com')
  ));


  const showPostIcon = !isComment && (!post.thumbnail || post.thumbnail === 'spoiler' || post.thumbnail === 'self' || post.thumbnail === 'default' || post.thumbnail === 'nsfw') && !isImageUrl;
  const showNSFWIcon = !isComment && (post.thumbnail === 'nsfw') && !isImageUrl;
  const showThumbnail = !isComment && post.thumbnail && !showPostIcon && !showNSFWIcon && !isImageUrl;
  const showImage = !isComment && isImageUrl;

  // Determine if we have actual media content
  const hasActualMedia = showImage || showThumbnail;
  const hasOnlyDefaultIcons = showPostIcon || showNSFWIcon;

  return (
    <div 
      className={`reddit-card shadow-sm ${isComment ? 'reddit-comment' : ''} cursor-pointer transition-all duration-200 ${
        inTray 
          ? 'ring-2 ring-blue-500 dark:ring-blue-400 !bg-blue-50 dark:!bg-blue-900/20 border-blue-300 dark:border-blue-600' 
          : 'hover:shadow-md hover:!bg-gray-50 dark:hover:!bg-gray-700/50'
      }`}
      onClick={handleCardClick}
      onTouchStart={handleLongPressStart}
      onTouchEnd={handleLongPressEnd}
      onMouseDown={handleLongPressStart}
      onMouseUp={handleLongPressEnd}
      onMouseLeave={handleLongPressEnd}
    >
      <FavoriteStar itemId={post.id} />
      <div className={`reddit-card-content ${hasActualMedia ? 'has-media' : ''}`}>
        {/* Column 1: Vote Section */}
        <div className="reddit-vote-section">
          <div className="reddit-upvote-button reddit-vote-button">
            <UpvoteIcon size={24} className="reddit-upvote-icon" />
          </div>
          <span className="reddit-vote-count">{post.ups}</span>
          <div className="reddit-downvote-button reddit-vote-button">
            <DownvoteIcon size={24} className="reddit-downvote-icon" />
          </div>
        </div>

        {/* Column 2: Image or Icon */}
        <div className={`reddit-image-or-icon ${hasOnlyDefaultIcons ? 'mobile-hide' : ''}`}>
          {showImage && (
            <img
              src={post.image_url || post.url}
              alt={post.title}
              className="reddit-post-image cursor-zoom-in"
              onClick={(e) => { e.stopPropagation(); setPreviewImage([{ src: post.image_url || post.url, alt: post.title, type: 'photo' }]); }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            />
          )}
          {showThumbnail && (
            <img
              src={post.thumbnail}
              alt="Post thumbnail"
              className="reddit-post-thumbnail cursor-zoom-in"
              onClick={(e) => { e.stopPropagation(); setPreviewImage([{ src: post.thumbnail, alt: post.title, type: 'photo' }]); }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            />
          )}
          {showPostIcon && (
            <div className="reddit-icon-container">
              <PostIcon size={40} />
            </div>
          )}
          {showNSFWIcon && (
            <div className="reddit-icon-container">
              <NSFWIcon size={40} />
            </div>
          )}
        </div>

        {/* Column 3: Post Content */}
        <div className="reddit-post-content">
          {!isComment && (
            <h3 className="reddit-post-title">
              <a href={post.url} target="_blank" rel="noopener noreferrer" className="reddit-link">
                {post.title}
              </a>
            </h3>
          )}
          {isComment && post.title && (
            <a href={post.url} target="_blank" rel="noopener noreferrer" className="reddit-link">
              {post.title}
            </a>
          )}

          <div className="flex items-center justify-between">
            <p className="reddit-post-meta">
              {isComment ? 'Comment by ' : 'Posted by '}
              <a
                href={`https://www.reddit.com/user/${post.author}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="tweet-handle"
              >
                u/{post.author}
              </a>
              {' in '}
              <SearchLink
                operator="subreddit"
                value={post.subreddit}
                className="tweet-handle"
                showParens={false}
              >
                r/{post.subreddit}
              </SearchLink>
            </p>
          </div>

          <div className="reddit-post-text">
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer" className="reddit-link" aria-label="Reddit" />
                ),
                p: ({ node, ...props }) => <p {...props} className="mb-2" />,
              }}
            >
              {post.text || post.body}
            </ReactMarkdown>
          </div>

          {/* Footer forced to bottom */}
          <div className="reddit-post-footer">
            <a href={post.url} target="_blank" rel="noopener noreferrer" className="reddit-footer-action">
              <MessageCircle size={18} className="mr-2" />
              <span>{post.num_comments} comments</span>
            </a>
            {/* <button className="reddit-footer-action">
              <Share2 size={18} className="mr-2" />
              <span>Share</span>
            </button> */}
            <a
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              className="reddit-footer-action"
              aria-label="Visit Reddit"
            >
              <RedditLogo size={16} />
              <span className="sr-only">Reddit</span>
            </a>
          </div>
        </div>

      </div>

      {previewImage && (
        <ImagePreviewModal
          mediaList={previewImage}
          initialIndex={0}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );

};

export default RedditCard;